import { versionedAssetUrl } from '@sorrem/engine';
import { createDefaultAudioSettings, createLogger, parseSoundCatalog, SOUND_CHANNELS, type AudioSettings, type SoundCatalog, type SoundChannel } from '@sorrem/shared';
import type { SoundCommand } from '@sorrem-client/types';
import { listCatalogFiles, pickCueVariant, resolveCue, type ResolvedCue } from './sound-catalog';

const logger = createLogger('client:page-audio');

/** The sound cue catalog (game-content/data/sounds.json). */
const CATALOG_URL = '/assets/sounds';
/** Cue files are paths under `/assets/`. */
const ASSET_BASE_URL = '/assets/';
const DEFAULT_MAX_VOICES = 4;
/** How long the ambience bed takes to crossfade, in seconds. */
const AMBIENCE_FADE_SECONDS = 1.5;
/** How long a stopped loop takes to fade out, so it doesn't click. */
const LOOP_FADE_SECONDS = 0.2;
/** A one-shot whose file is still loading this long after it was asked for is dropped: it would land late. */
const MAX_LATE_MS = 250;
/** How quickly a volume change settles (setTargetAtTime's time constant). */
const VOLUME_SMOOTHING_SECONDS = 0.05;

interface Voice {
    readonly source: AudioBufferSourceNode;
    readonly gain: GainNode;
}

/** How the Sound Debugger auditions a file: the channel it plays on, and its volume, rate and pan. */
export interface PreviewFileOptions {
    readonly channel: SoundChannel;
    readonly volume?: number;
    readonly rate?: number;
    readonly pan?: number;
}

/** A sound the Sound Debugger started: stop it early, or hear when it has finished. */
export interface PreviewHandle {
    stop(): void;
    /** Settles once it has stopped, by itself or by `stop`. */
    readonly ended: Promise<void>;
}

interface PlayOptions {
    readonly pan: number;
    readonly gain: number;
    /** Fades in over this many seconds instead of starting at full volume. */
    readonly fadeInSeconds?: number;
}

/**
 * The page's audio (#260): the one `AudioContext`, a gain per mix channel
 * under a master gain, and the voices playing on them. Everything that
 * makes a sound on the page goes through here: the game and its scenes
 * (as `SoundCommand`s relayed from their workers), the debuggers, UI
 * clicks, and cutscenes (`connectMediaElement`).
 *
 * The browser keeps a page's audio suspended until the player interacts
 * with it, so the context resumes on the first pointer or key press;
 * sounds asked for before then are dropped, ambience is kept for when it
 * resumes. A missing catalog or file only logs: sound is an extra, and the
 * game must still play silently.
 */
export class PageAudio {
    private context: AudioContext | null = null;
    private master: GainNode | null = null;
    private readonly channelGains = new Map<SoundChannel, GainNode>();
    private settings: AudioSettings = createDefaultAudioSettings();
    private catalog: SoundCatalog | null = null;
    private catalogLoad: Promise<SoundCatalog | null> | null = null;
    private readonly buffers = new Map<string, Promise<AudioBuffer | null>>();
    /** Each cue's voices still playing, oldest first, for its voice cap. */
    private readonly voices = new Map<string, Voice[]>();
    private readonly lastPlayedAtMs = new Map<string, number>();
    private readonly loops = new Map<string, Voice>();
    /** The latest start asked for each loop, while it loads: a stop, or a newer start, replaces it. */
    private readonly loopRequests = new Map<string, symbol>();
    /** Files the Sound Debugger is auditioning (`previewFile`). */
    private readonly previews = new Set<Voice>();
    /** The ambience bed: the cues asked for, and the voice playing them once one is. */
    private ambienceCues: readonly string[] | null = null;
    private ambience: { readonly cueId: string; readonly voice: Voice } | null = null;

    constructor(private readonly target: Window) {
        const unlock = (): void => {
            const context = this.ensureContext();
            if (context === null) {
                return;
            }
            void context.resume().then(() => {
                this.target.removeEventListener('pointerdown', unlock, true);
                this.target.removeEventListener('keydown', unlock, true);
                this.preload();
                void this.applyAmbience();
            }).catch(() => undefined);
        };
        target.addEventListener('pointerdown', unlock, true);
        target.addEventListener('keydown', unlock, true);
    }

    /**
     * Loads the catalog, and once the page has audio (its first interaction)
     * every file it plays, so the first of each sound isn't late. Safe to call again.
     */
    preload(): void {
        void this.loadCatalog().then((catalog) => {
            if (catalog !== null && this.context !== null) {
                for (const file of listCatalogFiles(catalog)) {
                    void this.loadBuffer(file);
                }
            }
        });
    }

    handle(command: SoundCommand): void {
        switch (command.kind) {
            case 'play':
                void this.playCue(command.cues, { pan: command.pan, gain: command.gain });
                return;
            case 'loop-start':
                void this.startLoop(command.loopId, command.cues, { pan: command.pan, gain: command.gain });
                return;
            case 'loop-stop':
                this.stopLoop(command.loopId);
                return;
            case 'ambience':
                this.ambienceCues = command.cues;
                void this.applyAmbience();
                return;
            case 'stop-all':
                for (const loopId of [...this.loops.keys()]) {
                    this.stopLoop(loopId);
                }
                this.ambienceCues = null;
                void this.applyAmbience();
                return;
            default: {
                // A new command kind must be handled here, not silently ignored.
                const unhandled: never = command;
                throw new Error(`Unhandled sound command: ${JSON.stringify(unhandled)}`);
            }
        }
    }

    /** Plays the first of `cues` the catalog defines, centered and at full volume (UI sounds). */
    play(...cues: readonly string[]): void {
        this.handle({ kind: 'play', cues, pan: 0, gain: 1 });
    }

    /**
     * Wakes the page's audio, for a click that should be heard at once (the
     * Sound Debugger's play buttons): the unlock on the same click resumes it
     * too, but a command sent before that settles would be dropped.
     */
    async resume(): Promise<void> {
        await this.ensureContext()?.resume().catch(() => undefined);
    }

    /**
     * Plays a catalog cue for the Sound Debugger as the game would (a file
     * picked from it, its volume, pitch, channel and looping), but with a
     * handle to stop it. Resolves null when the catalog has no such cue.
     */
    async previewCue(cueId: string, options: { readonly pan: number; readonly gain: number }): Promise<PreviewHandle | null> {
        await this.resume();
        const cue = await this.findCue([cueId]);
        if (cue === null) {
            return null;
        }
        const voice = await this.startVoice(cue, options, false);
        return voice === null ? null : this.trackPreview(voice);
    }

    /**
     * Plays a sound file's bytes once on a channel, outside the catalog: the
     * Sound Debugger auditioning a file. Resolves null when it can't decode.
     */
    async previewFile(bytes: ArrayBuffer, options: PreviewFileOptions): Promise<PreviewHandle | null> {
        await this.resume();
        const context = this.context;
        const output = this.channelGains.get(options.channel);
        if (context === null || output === undefined) {
            return null;
        }
        let buffer: AudioBuffer;
        try {
            buffer = await context.decodeAudioData(bytes);
        } catch (error) {
            logger.warn('could not decode a previewed sound file:', error);
            return null;
        }
        const source = context.createBufferSource();
        source.buffer = buffer;
        source.playbackRate.value = options.rate ?? 1;
        const gain = context.createGain();
        gain.gain.value = Math.max(0, Math.min(1, options.volume ?? 1));
        const panner = context.createStereoPanner();
        panner.pan.value = Math.max(-1, Math.min(1, options.pan ?? 0));
        source.connect(gain).connect(panner).connect(output);
        source.start();
        return this.trackPreview({ source, gain });
    }

    /** Stops everything `previewCue` and `previewFile` are playing. */
    stopPreviews(): void {
        for (const voice of this.previews) {
            this.fadeOut(voice, LOOP_FADE_SECONDS);
        }
        this.previews.clear();
    }

    /** Keeps a preview's voice for `stopPreviews`, and hands back its handle. */
    private trackPreview(voice: Voice): PreviewHandle {
        this.previews.add(voice);
        const ended = new Promise<void>((resolve) => {
            voice.source.addEventListener('ended', () => {
                this.previews.delete(voice);
                resolve();
            });
        });
        return {
            stop: () => {
                if (this.previews.delete(voice)) {
                    this.fadeOut(voice, LOOP_FADE_SECONDS);
                }
            },
            ended,
        };
    }

    setSettings(settings: AudioSettings): void {
        this.settings = settings;
        this.applyVolumes();
    }

    /**
     * Routes an `<audio>` element (a cutscene's music) through a channel, so
     * its volume settings apply. Returns the disconnect.
     */
    connectMediaElement(element: HTMLMediaElement, channel: SoundChannel): () => void {
        const context = this.ensureContext();
        const output = this.channelGains.get(channel);
        if (context === null || output === undefined) {
            return () => undefined;
        }
        try {
            const source = context.createMediaElementSource(element);
            source.connect(output);
            return () => source.disconnect();
        } catch (error) {
            logger.warn('could not route a media element through the mixer:', error);
            return () => undefined;
        }
    }

    private ensureContext(): AudioContext | null {
        if (this.context !== null) {
            return this.context;
        }
        if (typeof AudioContext === 'undefined') {
            return null;
        }
        const context = new AudioContext();
        const master = context.createGain();
        master.connect(context.destination);
        for (const channel of SOUND_CHANNELS) {
            const gain = context.createGain();
            gain.connect(master);
            this.channelGains.set(channel, gain);
        }
        this.context = context;
        this.master = master;
        this.applyVolumes();
        return context;
    }

    private applyVolumes(): void {
        const context = this.context;
        if (context === null || this.master === null) {
            return;
        }
        const now = context.currentTime;
        this.master.gain.setTargetAtTime(this.settings.isMuted ? 0 : this.settings.master, now, VOLUME_SMOOTHING_SECONDS);
        for (const channel of SOUND_CHANNELS) {
            this.channelGains.get(channel)?.gain.setTargetAtTime(this.settings[channel], now, VOLUME_SMOOTHING_SECONDS);
        }
    }

    private loadCatalog(): Promise<SoundCatalog | null> {
        this.catalogLoad ??= fetch(CATALOG_URL, { headers: { Accept: 'application/json' } })
            .then(async (response) => {
                if (!response.ok) {
                    throw new Error(`HTTP ${response.status}`);
                }
                const { catalog, problems } = parseSoundCatalog(await response.json());
                if (problems.length > 0) {
                    logger.warn('sound catalog cues left out:', problems);
                }
                this.catalog = catalog;
                return this.catalog;
            })
            .catch((error: unknown) => {
                logger.warn('sound catalog did not load; the game plays silently:', error);
                return null;
            });
        return this.catalogLoad;
    }

    private loadBuffer(file: string): Promise<AudioBuffer | null> {
        const cached = this.buffers.get(file);
        if (cached !== undefined) {
            return cached;
        }
        // No context before the page's first interaction (making one early only earns a console warning).
        const context = this.context;
        if (context === null) {
            return Promise.resolve(null);
        }
        // Versioned (#285): a precached file comes straight from the cache.
        const loading = fetch(versionedAssetUrl(`${ASSET_BASE_URL}${file}`))
            .then(async (response) => {
                if (!response.ok) {
                    throw new Error(`HTTP ${response.status}`);
                }
                return context.decodeAudioData(await response.arrayBuffer());
            })
            .catch((error: unknown) => {
                logger.warn(`sound file "${file}" did not load:`, error);
                return null;
            });
        this.buffers.set(file, loading);
        return loading;
    }

    /** The cue `cues` names, once the catalog has loaded; null for none. */
    private async findCue(cues: readonly string[]): Promise<ResolvedCue | null> {
        const catalog = this.catalog ?? await this.loadCatalog();
        return catalog === null ? null : resolveCue(catalog, cues);
    }

    private async playCue(cues: readonly string[], options: PlayOptions): Promise<void> {
        const askedAtMs = performance.now();
        if (this.context?.state !== 'running') {
            return;
        }
        const cue = await this.findCue(cues);
        if (cue === null) {
            return;
        }
        const minIntervalMs = cue.definition.minIntervalMs ?? 0;
        if (askedAtMs - (this.lastPlayedAtMs.get(cue.id) ?? -Infinity) < minIntervalMs) {
            return;
        }
        this.lastPlayedAtMs.set(cue.id, askedAtMs);
        const voice = await this.startVoice(cue, options, false);
        if (voice === null) {
            return;
        }
        if (performance.now() - askedAtMs > MAX_LATE_MS) {
            voice.source.stop();
            return;
        }
        const playing = this.voices.get(cue.id) ?? [];
        playing.push(voice);
        this.voices.set(cue.id, playing);
        voice.source.addEventListener('ended', () => {
            const index = playing.indexOf(voice);
            if (index >= 0) {
                playing.splice(index, 1);
            }
        });
        // Over the cap, the oldest gives way to the new.
        while (playing.length > (cue.definition.maxVoices ?? DEFAULT_MAX_VOICES)) {
            playing.shift()?.source.stop();
        }
    }

    private async startLoop(loopId: string, cues: readonly string[], options: PlayOptions): Promise<void> {
        this.stopLoop(loopId);
        const request = Symbol(loopId);
        this.loopRequests.set(loopId, request);
        const cue = await this.findCue(cues);
        if (cue === null) {
            return;
        }
        const voice = await this.startVoice(cue, { ...options, fadeInSeconds: LOOP_FADE_SECONDS }, true);
        if (voice === null) {
            return;
        }
        // A stop, or a newer start, came while this one loaded: the newest wins, and a stop keeps it quiet.
        if (this.loopRequests.get(loopId) !== request) {
            this.fadeOut(voice, LOOP_FADE_SECONDS);
            return;
        }
        this.loopRequests.delete(loopId);
        this.loops.set(loopId, voice);
    }

    private stopLoop(loopId: string): void {
        const voice = this.loops.get(loopId);
        this.loops.delete(loopId);
        this.loopRequests.delete(loopId);
        if (voice) {
            this.fadeOut(voice, LOOP_FADE_SECONDS);
        }
    }

    /** Brings the playing ambience in line with what was last asked for, crossfading. */
    private async applyAmbience(): Promise<void> {
        const wanted = this.ambienceCues;
        const cue = wanted === null || this.context?.state !== 'running' ? null : await this.findCue(wanted);
        if (this.ambienceCues !== wanted || cue?.id === this.ambience?.cueId) {
            return;
        }
        if (this.ambience) {
            this.fadeOut(this.ambience.voice, AMBIENCE_FADE_SECONDS);
            this.ambience = null;
        }
        if (cue === null) {
            return;
        }
        const voice = await this.startVoice(cue, { pan: 0, gain: 1, fadeInSeconds: AMBIENCE_FADE_SECONDS }, true);
        if (voice === null) {
            return;
        }
        // Asked for something else (or another bed started) while this one loaded.
        if (this.ambienceCues !== wanted || this.ambience !== null) {
            this.fadeOut(voice, AMBIENCE_FADE_SECONDS);
            return;
        }
        this.ambience = { cueId: cue.id, voice };
    }

    private async startVoice(cue: ResolvedCue, options: PlayOptions, isLooping: boolean): Promise<Voice | null> {
        const context = this.context;
        const output = this.channelGains.get(cue.definition.channel);
        if (context === null || output === undefined) {
            return null;
        }
        // Presentation only, so Math.random rather than the gameplay SeededRng.
        const variant = pickCueVariant(cue.definition, Math.random);
        const buffer = await this.loadBuffer(variant.file);
        if (buffer === null) {
            return null;
        }
        const source = context.createBufferSource();
        source.buffer = buffer;
        source.loop = isLooping || cue.definition.isLooping === true;
        source.playbackRate.value = variant.rate;
        const gain = context.createGain();
        const volume = (cue.definition.volume ?? 1) * Math.max(0, Math.min(1, options.gain));
        if (options.fadeInSeconds !== undefined) {
            gain.gain.setValueAtTime(0, context.currentTime);
            gain.gain.linearRampToValueAtTime(volume, context.currentTime + options.fadeInSeconds);
        } else {
            gain.gain.value = volume;
        }
        const panner = context.createStereoPanner();
        panner.pan.value = Math.max(-1, Math.min(1, options.pan));
        source.connect(gain).connect(panner).connect(output);
        source.start();
        return { source, gain };
    }

    private fadeOut(voice: Voice, seconds: number): void {
        const context = this.context;
        if (context === null) {
            return;
        }
        const now = context.currentTime;
        voice.gain.gain.cancelScheduledValues(now);
        voice.gain.gain.setValueAtTime(voice.gain.gain.value, now);
        voice.gain.gain.linearRampToValueAtTime(0, now + seconds);
        voice.source.stop(now + seconds);
    }
}

let pageAudio: PageAudio | null = null;

/** The page's one audio service, made on first use. */
export function getPageAudio(): PageAudio {
    pageAudio ??= new PageAudio(window);
    return pageAudio;
}
